/**
 * Collection Sentiment Hook
 * 
 * React hook for collection sentiment voting (CoinMarketCap-style)
 * Handles optimistic updates and cooldown periods
 */

'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { VoteResponse, SentimentType, VOTING_CONSTANTS } from '@/types/voting';
import { calculateSentimentPercentages } from '@/lib/voting-utils';
import { useClientIdentity } from '@/lib/client-identity';

interface UseCollectionSentimentResult {
  bullishVotes: number;
  bearishVotes: number;
  totalVotes: number;
  bullishPercentage: number;
  bearishPercentage: number;
  userVote: SentimentType | null;
  isLoading: boolean;
  isVoting: boolean;
  error: string | null;
  canVote: boolean;
  cooldownSeconds: number;
  vote: (sentiment: SentimentType) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook to manage collection sentiment voting
 * @param collectionSlug Collection slug to track
 * @param autoLoad Whether to automatically load sentiment on mount (default: true)
 */
export function useCollectionSentiment(
  collectionSlug: string | undefined,
  autoLoad = true
): UseCollectionSentimentResult {
  const [bullishVotes, setBullishVotes] = useState(0);
  const [bearishVotes, setBearishVotes] = useState(0);
  const [userVote, setUserVote] = useState<SentimentType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  
  // Get client identity for better vote tracking
  const { clientId } = useClientIdentity();

  // Compute percentages from votes (no need to store in state)
  const { bullishPercentage, bearishPercentage, totalVotes } = useMemo(
    () => calculateSentimentPercentages(bullishVotes, bearishVotes),
    [bullishVotes, bearishVotes]
  );

  const canVote = cooldownSeconds === 0 && !isVoting;

  // Load sentiment data
  const loadSentiment = useCallback(async () => {
    if (!collectionSlug) return;

    setIsLoading(true);
    setError(null);

    try {
      const url = new URL('/api/votes/collection/sentiment', window.location.origin);
      url.searchParams.set('collectionSlug', collectionSlug);
      if (clientId) {
        url.searchParams.set('clientId', clientId);
      }
      const response = await fetch(url.toString());
      const data: VoteResponse = await response.json();

      if (data.success && data.data) {
        setBullishVotes(data.data.bullishVotes || 0);
        setBearishVotes(data.data.bearishVotes || 0);
        setUserVote(data.data.userVote || null);
        
        // Set cooldown from server response (per-collection cooldown)
        // This ensures correct UI state after page refresh
        if (data.data.cooldownRemaining && data.data.cooldownRemaining > 0) {
          setCooldownSeconds(data.data.cooldownRemaining);
        } else {
          setCooldownSeconds(0);
        }
      } else {
        setError(data.error || 'Failed to load sentiment data');
      }
    } catch (err) {
      console.error('Error loading sentiment:', err);
      setError('Failed to load sentiment data');
    } finally {
      setIsLoading(false);
    }
  }, [collectionSlug, clientId]);

  // Auto-load on mount or when collectionSlug changes
  // IMPORTANT: Reset cooldown when collection changes - cooldown is per-collection, not global
  useEffect(() => {
    if (autoLoad && collectionSlug) {
      // Reset cooldown when switching to a different collection
      setCooldownSeconds(0);
      loadSentiment();
    }
  }, [autoLoad, collectionSlug, loadSentiment]);

  // Vote function with optimistic updates
  const vote = useCallback(
    async (sentiment: SentimentType) => {
      if (!collectionSlug) {
        setError('No collection slug provided');
        return;
      }

      if (sentiment !== 'bullish' && sentiment !== 'bearish') {
        setError('Invalid sentiment');
        return;
      }

      if (!canVote) {
        setError(
          cooldownSeconds > 0
            ? `Please wait ${cooldownSeconds}s before voting again`
            : 'Please wait before voting again'
        );
        return;
      }

      setIsVoting(true);
      setError(null);

      // Save previous state for rollback
      const previousState = {
        bullishVotes,
        bearishVotes,
        userVote,
      };

      // Same vote - show message and return
      if (userVote === sentiment) {
        setIsVoting(false);
        setError('You have already voted with this sentiment');
        // Clear error after 3 seconds
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Calculate new votes
      let newBullishVotes = bullishVotes;
      let newBearishVotes = bearishVotes;

      if (userVote === null) {
        // New vote
        if (sentiment === 'bullish') {
          newBullishVotes++;
        } else {
          newBearishVotes++;
        }
      } else {
        // Changing vote
        if (sentiment === 'bullish') {
          newBullishVotes++;
          newBearishVotes--;
        } else {
          newBearishVotes++;
          newBullishVotes--;
        }
      }

      // Optimistic update
      setBullishVotes(newBullishVotes);
      setBearishVotes(newBearishVotes);
      setUserVote(sentiment);

      try {
        const body: any = {
          collectionSlug,
          sentiment,
        };
        if (clientId) {
          body.clientId = clientId;
        }

        const response = await fetch('/api/votes/collection/sentiment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        const data: VoteResponse = await response.json();

        if (data.success && data.data) {
          // Update with server response
          setBullishVotes(data.data.bullishVotes || 0);
          setBearishVotes(data.data.bearishVotes || 0);
          setUserVote(data.data.userVote || null);

          // Set cooldown (convert ms to seconds)
          setCooldownSeconds(VOTING_CONSTANTS.SENTIMENT_COOLDOWN / 1000);
        } else {
          // Revert on error
          setBullishVotes(previousState.bullishVotes);
          setBearishVotes(previousState.bearishVotes);
          setUserVote(previousState.userVote);
          setError(data.error || 'Failed to record vote');
          // Clear error after 5 seconds
          setTimeout(() => setError(null), 5000);

          // Set cooldown from server if available
          if (data.rateLimitReset) {
            setCooldownSeconds(data.rateLimitReset);
          }
        }
      } catch (err) {
        console.error('Error voting sentiment:', err);
        // Revert on error
        setBullishVotes(previousState.bullishVotes);
        setBearishVotes(previousState.bearishVotes);
        setUserVote(previousState.userVote);
        setError('Failed to record vote. Please try again.');
        // Clear error after 5 seconds
        setTimeout(() => setError(null), 5000);
      } finally {
        setIsVoting(false);
      }
    },
    [collectionSlug, bullishVotes, bearishVotes, userVote, canVote, cooldownSeconds, clientId]
  );

  return {
    bullishVotes,
    bearishVotes,
    totalVotes,
    bullishPercentage,
    bearishPercentage,
    userVote,
    isLoading,
    isVoting,
    error,
    canVote,
    cooldownSeconds,
    vote,
    refresh: loadSentiment,
  };
}

